import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, LinearProgress, Typography } from '@material-ui/core';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { HIDEABLE_PAGE_GROUPS } from '../../../../../global/auth/use-hidden-pages/hideable-pages';
import { GET_SUB_ACCOUNT_HIDDEN_PAGES } from './enterprise-hidden-pages.gql';
import { GetSubAccountHiddenPages } from './__generated__/GetSubAccountHiddenPages';

const sameSelection = (a: string[], b: string[]) => a.length === b.length && a.every((pageId) => b.includes(pageId));

export function EnterpriseSubHiddenPages() {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const saveHiddenPages = useCampfireFetch<{ hiddenPages: string[] }>({ defer: true });
  const { data, loading } = useCampfireQuery<GetSubAccountHiddenPages, {}>(GET_SUB_ACCOUNT_HIDDEN_PAGES, {
    options: { fetchPolicy: 'network-only' },
  });

  const [saved, setSaved] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const hiddenPages = data?.tm.tenant.config?.subAccountHiddenPages ?? [];
    setSaved(hiddenPages);
    setSelected(hiddenPages);
  }, [data]);

  const togglePage = (pageId: string) =>
    setSelected((prev) => (prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]));

  const onSave = async () => {
    try {
      const response = await saveHiddenPages.run({
        url: `${apiUrl}/tm/enterprise/sub-accounts/hidden-pages`,
        method: 'post',
        data: { pageIds: selected },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSaved(selected);
      setSnackbar({ open: true, message: 'Hidden pages saved', variant: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Unable to save hidden pages', variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography style={{ fontWeight: 500, fontSize: '14px' }}>Hidden pages</Typography>
      <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
        {
          'Ticked pages are removed from the menu in every sub-account and cannot be opened by link. This account is unaffected. Changes reach a sub-account when it is created, or when pushed under Push Updates > Organisation settings.'
        }
      </Typography>

      {loading ? (
        <LinearProgress style={{ marginTop: '12px' }} />
      ) : (
        <Box display='flex' flexWrap='wrap' mt={1.5} style={{ gap: '12px 24px' }}>
          {HIDEABLE_PAGE_GROUPS.map(({ group, pages }) => (
            <Box key={group} style={{ width: '30%', minWidth: '220px' }}>
              <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '2px' }}>
                {group}
              </Typography>
              {pages.map((page) => (
                <FormControlLabel
                  key={page.id}
                  style={{ display: 'flex', marginRight: 0 }}
                  control={
                    <Checkbox
                      color='primary'
                      size='small'
                      checked={selected.includes(page.id)}
                      onChange={() => togglePage(page.id)}
                    />
                  }
                  label={<Typography style={{ fontSize: '13px' }}>{page.label}</Typography>}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}

      <Box mt={2}>
        <Button
          variant='contained'
          color='primary'
          disableElevation
          disabled={loading || saveHiddenPages.isLoading || sameSelection(saved, selected)}
          onClick={onSave}
        >
          Save hidden pages
        </Button>
      </Box>
    </Box>
  );
}
